// src/stores/auth.js - UPDATED VERSION
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/auth'
import { useDataStore } from './data'
import axios from 'axios'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const apiKey = ref('')
  const token = ref('')
  const tokenExpiry = ref(null)
  const authMethod = ref('password') // 'apiKey' or 'password'
  const baseUrl = ref(import.meta.env.VITE_API_BASE_URL || 'http://localhost:8005/api/v1')
  const isLoading = ref(false)
  const error = ref('')

  // Getters
  const isAuthenticated = computed(() => {
    if (authMethod.value === 'apiKey') {
      return !!apiKey.value
    } else {
      // Check if token exists and hasn't expired
      if (!token.value) return false
      if (tokenExpiry.value && Date.now() > tokenExpiry.value) {
        // Token expired
        logout()
        return false
      }
      return true
    }
  })

  const isTokenExpired = computed(() => {
    if (authMethod.value !== 'password' || !tokenExpiry.value) return false
    return Date.now() > tokenExpiry.value
  })

  // Actions
  const loginWithPassword = async (credentials) => {
    isLoading.value = true
    error.value = ''

    try {
      const response = await authService.loginWithPassword(credentials)

      if (response.success) {
        token.value = response.token
        baseUrl.value = credentials.baseUrl
        user.value = response.user
        authMethod.value = 'password'
        tokenExpiry.value = Date.now() + (response.expiresIn * 1000)

        // Store in localStorage
        localStorage.setItem('zeraki_auth', JSON.stringify({
          token: token.value,
          baseUrl: baseUrl.value,
          user: user.value,
          authMethod: 'password',
          tokenExpiry: tokenExpiry.value
        }))

        // Set default authorization header for axios
        axios.defaults.headers.common['Authorization'] = `Bearer ${token.value}`

        // Initialize data store
        const dataStore = useDataStore()
        await dataStore.loadInitialData()

        return { success: true }
      }
    } catch (err) {
      error.value = err.message || 'Login failed'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  const loginWithApiKey = async (credentials) => {
    isLoading.value = true
    error.value = ''

    try {
      const response = await authService.validateApiKey(credentials)

      if (response.success) {
        apiKey.value = credentials.apiKey
        baseUrl.value = credentials.baseUrl
        user.value = response.user || { name: 'API User' }
        authMethod.value = 'apiKey'

        // Store in localStorage
        localStorage.setItem('zeraki_auth', JSON.stringify({
          apiKey: apiKey.value,
          baseUrl: baseUrl.value,
          user: user.value,
          authMethod: 'apiKey'
        }))

        // Set default API key header for axios
        axios.defaults.headers.common['x-api-key'] = apiKey.value

        // Initialize data store
        const dataStore = useDataStore()
        await dataStore.loadInitialData()

        return { success: true }
      }
    } catch (err) {
      error.value = err.message || 'Authentication failed'
      return { success: false, error: error.value }
    } finally {
      isLoading.value = false
    }
  }

  // Legacy method for backwards compatibility
  const login = async (credentials) => {
    if (credentials.apiKey) {
      return await loginWithApiKey(credentials)
    } else if (credentials.username && credentials.password) {
      return await loginWithPassword(credentials)
    } else {
      return { success: false, error: 'Invalid credentials format' }
    }
  }

  const logout = () => {
    user.value = null
    apiKey.value = ''
    token.value = ''
    tokenExpiry.value = null
    authMethod.value = 'password'
    baseUrl.value = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8005/api/v1'
    isLoading.value = false
    error.value = ''

    // Clear localStorage
    localStorage.removeItem('zeraki_auth')

    // Clear axios headers
    delete axios.defaults.headers.common['Authorization']
    delete axios.defaults.headers.common['x-api-key']

    // Clear data store
    const dataStore = useDataStore()
    dataStore.clearData()
  }

  const restoreAuth = () => {
    try {
      const saved = localStorage.getItem('zeraki_auth')
      if (saved) {
        const data = JSON.parse(saved)
        baseUrl.value = data.baseUrl
        user.value = data.user
        authMethod.value = data.authMethod || 'password'

        if (data.authMethod === 'apiKey') {
          apiKey.value = data.apiKey
          axios.defaults.headers.common['x-api-key'] = apiKey.value

          // Validate the restored API key
          authService.validateApiKey({
            apiKey: apiKey.value,
            baseUrl: baseUrl.value
          }).then(response => {
            if (!response.success) {
              logout()
            } else {
              // Load data if auth is valid
              const dataStore = useDataStore()
              dataStore.loadInitialData()
            }
          }).catch(() => {
            logout()
          })
        } else if (data.authMethod === 'password') {
          token.value = data.token
          tokenExpiry.value = data.tokenExpiry

          // Check if token expired
          if (isTokenExpired.value) {
            logout()
            return
          }

          axios.defaults.headers.common['Authorization'] = `Bearer ${token.value}`

          // Validate the restored token
          authService.validateToken(baseUrl.value, token.value)
            .then(isValid => {
              if (!isValid) {
                logout()
              } else {
                // Load data if auth is valid
                const dataStore = useDataStore()
                dataStore.loadInitialData()
              }
            }).catch(() => {
              logout()
            })
        }
      }
    } catch (err) {
      console.error('Failed to restore auth:', err)
      logout()
    }
  }

  const refreshUserData = async () => {
    if (authMethod.value === 'password' && token.value) {
      try {
        const response = await authService.getCurrentUser(baseUrl.value, token.value)
        if (response.success) {
          user.value = response.user
          // Update localStorage
          const saved = JSON.parse(localStorage.getItem('zeraki_auth') || '{}')
          saved.user = user.value
          localStorage.setItem('zeraki_auth', JSON.stringify(saved))
        }
      } catch (err) {
        console.error('Failed to refresh user data:', err)
      }
    }
  }

  return {
    // State
    user,
    apiKey,
    token,
    tokenExpiry,
    authMethod,
    baseUrl,
    isLoading,
    error,

    // Getters
    isAuthenticated,
    isTokenExpired,

    // Actions
    login,  // Legacy method
    loginWithPassword,
    loginWithApiKey,
    logout,
    restoreAuth,
    refreshUserData
  }
})
